<div class="page">
  <header class="page-header">
    <h1 class="page-title">Settings</h1>
    <p class="page-subtitle">Manage your profile and preferences</p>
  </header>

  <div *ngIf="errorMessage && !loading" class="banner banner-error" role="alert">{{ errorMessage }}</div>
  <div *ngIf="successMessage" class="banner banner-success" role="status">{{ successMessage }}</div>

  <div *ngIf="loading" class="loading-state">
    <app-loader label="Loading settings"></app-loader>
  </div>

  <ng-container *ngIf="!loading">
    <section class="card" aria-labelledby="profile-card-title">
      <div class="card-header">
        <span class="card-icon" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </span>
        <h2 id="profile-card-title" class="card-title">Profile Information</h2>
      </div>

      <div class="card-body">
        <label class="field">
          <span class="field-label">Full Name</span>
          <input type="text" class="field-input" [(ngModel)]="displayName" name="displayName" maxlength="200" [disabled]="saving" />
        </label>

        <label class="field">
          <span class="field-label">Department</span>
          <select class="field-input field-select" [(ngModel)]="departmentId" name="departmentId" [disabled]="saving">
            <option [ngValue]="''">Select department</option>
            <option *ngFor="let d of departments" [ngValue]="d.id">{{ d.name }}</option>
          </select>
        </label>

        <label class="field">
          <span class="field-label">Email</span>
          <input type="email" class="field-input field-input-readonly" [value]="email" readonly disabled />
          <span class="field-hint">Email cannot be changed</span>
        </label>

        <div class="card-actions">
          <button type="button" class="btn-primary" (click)="save()" [disabled]="saving">
            {{ saving ? 'Saving…' : 'Save Changes' }}
          </button>
        </div>
      </div>
    </section>

    <section class="card notification-card" aria-labelledby="notification-prefs-title">
      <div class="card-header card-header-bell">
        <span class="card-icon card-icon-bell" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"
            />
          </svg>
        </span>
        <h2 id="notification-prefs-title" class="card-title">Notification Preferences</h2>
      </div>

      <div class="card-body prefs-list">
        <div class="pref-item">
          <div class="pref-text">
            <span class="pref-label">Email notifications</span>
            <span class="pref-desc">Receive email updates for your ideas</span>
          </div>
          <button
            type="button"
            class="toggle"
            [class.on]="emailNotifications"
            (click)="toggleEmailNotifications()"
            [disabled]="savingNotificationPrefs"
            aria-label="Toggle email notifications"
          >
            <span class="toggle-track"><span class="toggle-thumb"></span></span>
          </button>
        </div>
        <div class="pref-item">
          <div class="pref-text">
            <span class="pref-label">Idea status changes</span>
            <span class="pref-desc">Get notified when your ideas change status</span>
          </div>
          <button
            type="button"
            class="toggle"
            [class.on]="ideaStatusChanges"
            (click)="toggleIdeaStatusChanges()"
            [disabled]="savingNotificationPrefs"
            aria-label="Toggle idea status notifications"
          >
            <span class="toggle-track"><span class="toggle-thumb"></span></span>
          </button>
        </div>
        <div class="pref-item">
          <div class="pref-text">
            <span class="pref-label">Comments on my ideas</span>
            <span class="pref-desc">Notify when someone comments on your idea</span>
          </div>
          <button
            type="button"
            class="toggle"
            [class.on]="newComments"
            (click)="toggleNewComments()"
            [disabled]="savingNotificationPrefs"
            aria-label="Toggle comment notifications"
          >
            <span class="toggle-track"><span class="toggle-thumb"></span></span>
          </button>
        </div>
        <div class="pref-item">
          <div class="pref-text">
            <span class="pref-label">Followed idea updates</span>
            <span class="pref-desc">Updates for ideas you're following</span>
          </div>
          <button
            type="button"
            class="toggle"
            [class.on]="followedIdeaUpdates"
            (click)="toggleFollowedIdeaUpdates()"
            [disabled]="savingNotificationPrefs"
            aria-label="Toggle followed idea notifications"
          >
            <span class="toggle-track"><span class="toggle-thumb"></span></span>
          </button>
        </div>
        <div class="pref-item">
          <div class="pref-text">
            <span class="pref-label">Challenge announcements</span>
            <span class="pref-desc">New challenges and deadlines</span>
          </div>
          <button
            type="button"
            class="toggle"
            [class.on]="challengeAnnouncements"
            (click)="toggleChallengeAnnouncements()"
            [disabled]="savingNotificationPrefs"
            aria-label="Toggle challenge notifications"
          >
            <span class="toggle-track"><span class="toggle-thumb"></span></span>
          </button>
        </div>
        <div class="pref-item">
          <div class="pref-text">
            <span class="pref-label">Weekly digest</span>
            <span class="pref-desc">Weekly summary of platform activity</span>
          </div>
          <button
            type="button"
            class="toggle"
            [class.on]="weeklyDigest"
            (click)="toggleWeeklyDigest()"
            [disabled]="savingNotificationPrefs"
            aria-label="Toggle weekly digest"
          >
            <span class="toggle-track"><span class="toggle-thumb"></span></span>
          </button>
        </div>
      </div>
    </section>

    <section *ngIf="isAdmin" class="card admin-perms-card" aria-labelledby="admin-perms-title">
      <div class="card-header">
        <span class="card-icon" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 11c1.5 0 3-1.2 3-3.5S15 4 12 4 9 5.2 9 7.5 10.5 11 12 11Z" />
            <path d="M4.5 20.5C5.2 16 8 14 12 14s6.7 2 7.5 6.5" />
          </svg>
        </span>
        <h2 id="admin-perms-title" class="card-title">User permissions</h2>
      </div>
      <p class="card-lead">Assign platform roles to users. Changes apply on next API request; you may need to sign out and back in to refresh your own token.</p>
      <p *ngIf="adminSectionError" class="banner banner-error" role="alert">{{ adminSectionError }}</p>
      <p *ngIf="adminSectionSuccess" class="banner banner-success" role="status">{{ adminSectionSuccess }}</p>

      <div class="admin-users-table-wrap" *ngIf="adminUsers.length; else noAdminUsers">
        <table class="admin-users-table">
          <thead>
            <tr>
              <th scope="col">User</th>
              <th scope="col">Email</th>
              <th scope="col" class="roles-col">Roles</th>
              <th scope="col" class="actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let u of adminUsers">
              <td>
                <span class="user-name">{{ u.displayName || '—' }}</span>
                <span *ngIf="!u.isActive" class="badge-inactive">Inactive</span>
              </td>
              <td class="user-email">{{ u.email }}</td>
              <td class="roles-col">
                <ng-container *ngIf="roleCatalog.length; else noRolesCat">
                  <div class="role-chips">
                    <label
                      *ngFor="let r of roleCatalog"
                      class="role-check"
                      [class.role-check-on]="hasRoleDraft(u.userId, r.code)"
                    >
                      <input
                        type="checkbox"
                        [checked]="hasRoleDraft(u.userId, r.code)"
                        (change)="toggleUserRole(u.userId, r.code, $any($event.target).checked)"
                        [disabled]="savingRolesForUserId === u.userId"
                      />
                      <span [title]="r.name">{{ r.code }}</span>
                    </label>
                  </div>
                </ng-container>
                <ng-template #noRolesCat>
                  <span class="muted">No roles in catalog.</span>
                </ng-template>
              </td>
              <td class="actions-col">
                <button
                  type="button"
                  class="btn-primary btn-sm"
                  (click)="saveUserRoles(u.userId)"
                  [disabled]="savingRolesForUserId === u.userId"
                >
                  {{ savingRolesForUserId === u.userId ? 'Saving…' : 'Save' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <ng-template #noAdminUsers>
        <p class="muted">No users found.</p>
      </ng-template>
    </section>

    <section class="card stats-card" aria-labelledby="stats-title">
      <div class="card-header">
        <h2 id="stats-title" class="card-title">Your Stats</h2>
      </div>
      <div class="card-body stats-grid">
        <div class="stat-block">
          <span class="stat-value stat-value-blue">{{ stats.totalPoints | number }}</span>
          <span class="stat-label">Total Points</span>
          <span class="stat-hint">50 pts per delivered idea</span>
        </div>
        <div class="stat-block">
          <span class="stat-value stat-value-dark">{{ stats.ideasSubmitted | number }}</span>
          <span class="stat-label">Ideas Submitted</span>
        </div>
      </div>
    </section>

  </ng-container>
</div>
